// ShortcodeParser.ts - Parse shortcodes from HTML content

export interface ParsedShortcode {
  type: 'diretta-link' | 'pagelle-link' | 'formazione-tifosi' | 'formazione-risultati' | 
        'pagelle' | 'galleria' | 'social' | 'media' | 'youtube-iframe';
  matchId?: string;
  platform?: string;
  permalink?: string;
  url?: string;
  content?: string;
  originalMatch: string;
}

export interface ParsedContent {
  segments: Array<{type: 'html' | 'shortcode'; content: string; shortcode?: ParsedShortcode}>;
}

// All shortcode patterns
const SHORTCODE_PATTERNS = {
  // [diretta-link match="ID"]text[/diretta-link] or [diretta-link]
  direttaLink: /\[diretta-link(?:\s+match\s*=\s*["']?([^"'\]]+)["']?)?\]([\s\S]*?)?\[\/diretta-link\]|\[diretta-link(?:\s+match\s*=\s*["']?([^"'\]]+)["']?)?\]/gi,
  
  // [pagelle-link match="ID"]text[/pagelle-link] or [pagelle-link]
  pagelleLink: /\[pagelle-link(?:\s+match\s*=\s*["']?([^"'\]]+)["']?)?\]([\s\S]*?)?\[\/pagelle-link\]|\[pagelle-link(?:\s+match\s*=\s*["']?([^"'\]]+)["']?)?\]/gi,
  
  // [formazione_tifosi]
  formazioneTifosi: /\[formazione_tifosi\]/gi,
  
  // [formazione_risultati match="ID"] or [formazione_risultati]
  formazioneRisultati: /\[formazione_risultati(?:\s+match\s*=\s*["']?([^"'\]]+)["']?)?\]/gi,
  
  // [pagelle match="ID"]
  pagelle: /\[pagelle\s+match\s*=\s*["']?([^"'\]]+)["']?\]/gi,
  
  // [galleria]
  galleria: /\[galleria\]/gi,
  
  // [Social platform="X" permalink="URL"]
  social: /\[Social\s+platform\s*=\s*["']?(\w+)["']?\s+permalink\s*=\s*["']?([^"'\]]+)["']?\s*\]/gi,
  
  // [media url="URL"]
  media: /\[media\s+url\s*=\s*["']?([^"'\]]+)["']?\s*\]/gi,
  
  // YouTube iframe embeds
  youtubeIframe: /<iframe[^>]+src=["'](?:https?:)?\/\/(?:www\.)?(?:youtube\.com\/embed\/|youtu\.be\/)([a-zA-Z0-9_-]+)[^"']*["'][^>]*><\/iframe>/gi,
};

// API-generated HTML div shortcode patterns (the API transforms raw shortcodes into these)
const HTML_DIV_SHORTCODE_PATTERNS = {
  // <div data-shortcode="diretta-link" data-match_id="X"></div>
  direttaLink: /<div[^>]+data-shortcode="diretta-link"[^>]*data-match_id="([^"]+)"[^>]*><\/div>/gi,
  // <div data-shortcode="pagelle-link" data-match_id="X"></div>
  pagelleLink: /<div[^>]+data-shortcode="pagelle-link"[^>]*data-match_id="([^"]+)"[^>]*><\/div>/gi,
  // <div data-shortcode="pagelle" data-match_id="X"></div>
  pagelle: /<div[^>]+data-shortcode="pagelle"[^>]*data-match_id="([^"]+)"[^>]*><\/div>/gi,
  // <div data-shortcode="formazione_risultati" data-match_id="X"></div>
  formazioneRisultati: /<div[^>]+data-shortcode="formazione_risultati"[^>]*data-match_id="([^"]+)"[^>]*><\/div>/gi,
};

function extractYouTubeId(url: string): string | null {
  const patterns = [
    /(?:youtube\.com\/embed\/)([a-zA-Z0-9_-]+)/,
    /(?:youtube\.com\/watch\?v=)([a-zA-Z0-9_-]+)/,
    /(?:youtu\.be\/)([a-zA-Z0-9_-]+)/,
  ];
  
  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match) return match[1];
  }
  return null;
}

function detectMediaPlatform(url: string): {platform: string; type: 'social' | 'video'} {
  const lowUrl = url.toLowerCase();
  if (lowUrl.includes('youtube.com') || lowUrl.includes('youtu.be')) {
    return {platform: 'youtube', type: 'video'};
  }
  if (lowUrl.includes('twitter.com') || lowUrl.includes('x.com')) {
    return {platform: 'twitter', type: 'social'};
  }
  if (lowUrl.includes('instagram.com')) {
    return {platform: 'instagram', type: 'social'};
  }
  if (lowUrl.includes('facebook.com') || lowUrl.includes('fb.com')) {
    return {platform: 'facebook', type: 'social'};
  }
  return {platform: 'unknown', type: 'social'};
}

export function parseShortcodes(html: string): ParsedContent {
  const shortcodes: Array<{index: number; length: number; shortcode: ParsedShortcode}> = [];
  
  // Find all diretta-link shortcodes
  let match: RegExpExecArray | null;
  const direttaRegex = new RegExp(SHORTCODE_PATTERNS.direttaLink.source, 'gi');
  while ((match = direttaRegex.exec(html)) !== null) {
    shortcodes.push({
      index: match.index,
      length: match[0].length,
      shortcode: {
        type: 'diretta-link',
        matchId: match[1] || match[3],
        content: match[2],
        originalMatch: match[0],
      },
    });
  }
  
  // Find all pagelle-link shortcodes
  const pagelleLinkRegex = new RegExp(SHORTCODE_PATTERNS.pagelleLink.source, 'gi');
  while ((match = pagelleLinkRegex.exec(html)) !== null) {
    shortcodes.push({
      index: match.index,
      length: match[0].length,
      shortcode: {
        type: 'pagelle-link',
        matchId: match[1] || match[3],
        content: match[2],
        originalMatch: match[0],
      },
    });
  }
  
  // Find formazione_tifosi
  const tifosiRegex = new RegExp(SHORTCODE_PATTERNS.formazioneTifosi.source, 'gi');
  while ((match = tifosiRegex.exec(html)) !== null) {
    shortcodes.push({
      index: match.index,
      length: match[0].length,
      shortcode: {
        type: 'formazione-tifosi',
        originalMatch: match[0],
      },
    });
  }
  
  // Find formazione_risultati
  const risultatiRegex = new RegExp(SHORTCODE_PATTERNS.formazioneRisultati.source, 'gi');
  while ((match = risultatiRegex.exec(html)) !== null) {
    shortcodes.push({
      index: match.index,
      length: match[0].length,
      shortcode: {
        type: 'formazione-risultati',
        matchId: match[1],
        originalMatch: match[0],
      },
    });
  }
  
  // Find pagelle
  const pagelleRegex = new RegExp(SHORTCODE_PATTERNS.pagelle.source, 'gi');
  while ((match = pagelleRegex.exec(html)) !== null) {
    shortcodes.push({
      index: match.index,
      length: match[0].length,
      shortcode: {
        type: 'pagelle',
        matchId: match[1],
        originalMatch: match[0],
      },
    });
  }
  
  // Find galleria
  const galleriaRegex = new RegExp(SHORTCODE_PATTERNS.galleria.source, 'gi');
  while ((match = galleriaRegex.exec(html)) !== null) {
    shortcodes.push({
      index: match.index,
      length: match[0].length,
      shortcode: {
        type: 'galleria',
        originalMatch: match[0],
      },
    });
  }
  
  // Find Social shortcodes
  const socialRegex = new RegExp(SHORTCODE_PATTERNS.social.source, 'gi');
  while ((match = socialRegex.exec(html)) !== null) {
    shortcodes.push({
      index: match.index,
      length: match[0].length,
      shortcode: {
        type: 'social',
        platform: match[1].toLowerCase(),
        permalink: match[2],
        originalMatch: match[0],
      },
    });
  }
  
  // Find media shortcodes
  const mediaRegex = new RegExp(SHORTCODE_PATTERNS.media.source, 'gi');
  while ((match = mediaRegex.exec(html)) !== null) {
    const url = match[1];
    const detected = detectMediaPlatform(url);
    
    if (detected.platform === 'youtube') {
      shortcodes.push({
        index: match.index,
        length: match[0].length,
        shortcode: {
          type: 'youtube-iframe',
          url: url,
          originalMatch: match[0],
        },
      });
    } else {
      shortcodes.push({
        index: match.index,
        length: match[0].length,
        shortcode: {
          type: 'social',
          platform: detected.platform,
          permalink: url,
          originalMatch: match[0],
        },
      });
    }
  }
  
  // Find YouTube iframes
  const youtubeRegex = new RegExp(SHORTCODE_PATTERNS.youtubeIframe.source, 'gi');
  while ((match = youtubeRegex.exec(html)) !== null) {
    shortcodes.push({
      index: match.index,
      length: match[0].length,
      shortcode: {
        type: 'youtube-iframe',
        url: `https://www.youtube.com/watch?v=${match[1]}`,
        originalMatch: match[0],
      },
    });
  }
  
  // Parse HTML div-based shortcodes from the API
  const divDirettaRegex = new RegExp(HTML_DIV_SHORTCODE_PATTERNS.direttaLink.source, 'gi');
  while ((match = divDirettaRegex.exec(html)) !== null) {
    shortcodes.push({
      index: match.index,
      length: match[0].length,
      shortcode: {
        type: 'diretta-link',
        matchId: match[1],
        originalMatch: match[0],
      },
    });
  }

  const divPagelleLinkRegex = new RegExp(HTML_DIV_SHORTCODE_PATTERNS.pagelleLink.source, 'gi');
  while ((match = divPagelleLinkRegex.exec(html)) !== null) {
    shortcodes.push({
      index: match.index,
      length: match[0].length,
      shortcode: {
        type: 'pagelle-link',
        matchId: match[1],
        originalMatch: match[0],
      },
    });
  }

  const divPagelleRegex = new RegExp(HTML_DIV_SHORTCODE_PATTERNS.pagelle.source, 'gi');
  while ((match = divPagelleRegex.exec(html)) !== null) {
    shortcodes.push({
      index: match.index,
      length: match[0].length,
      shortcode: {
        type: 'pagelle',
        matchId: match[1],
        originalMatch: match[0],
      },
    });
  }

  const divRisultatiRegex = new RegExp(HTML_DIV_SHORTCODE_PATTERNS.formazioneRisultati.source, 'gi');
  while ((match = divRisultatiRegex.exec(html)) !== null) {
    shortcodes.push({
      index: match.index,
      length: match[0].length,
      shortcode: {
        type: 'formazione-risultati',
        matchId: match[1],
        originalMatch: match[0],
      },
    });
  }

  // Sort by index
  shortcodes.sort((a, b) => a.index - b.index);
  
  // Build segments
  const segments: ParsedContent['segments'] = [];
  let lastIndex = 0;
  
  for (const sc of shortcodes) {
    // Add HTML before this shortcode
    if (sc.index > lastIndex) {
      const htmlContent = html.slice(lastIndex, sc.index);
      if (htmlContent.trim()) {
        segments.push({type: 'html', content: htmlContent});
      }
    }
    
    // Add shortcode
    segments.push({type: 'shortcode', content: sc.shortcode.originalMatch, shortcode: sc.shortcode});
    
    lastIndex = sc.index + sc.length;
  }
  
  // Add remaining HTML
  if (lastIndex < html.length) {
    const htmlContent = html.slice(lastIndex);
    if (htmlContent.trim()) {
      segments.push({type: 'html', content: htmlContent});
    }
  }
  
  // If no shortcodes found, return single HTML segment
  if (segments.length === 0) {
    segments.push({type: 'html', content: html});
  }
  
  return {segments};
}

// Helper to check if content has any shortcodes
export function hasShortcodes(html: string): boolean {
  for (const pattern of Object.values(SHORTCODE_PATTERNS)) {
    const regex = new RegExp(pattern.source, 'i');
    if (regex.test(html)) {
      return true;
    }
  }
  // Also check for API-generated HTML div shortcodes
  for (const pattern of Object.values(HTML_DIV_SHORTCODE_PATTERNS)) {
    const regex = new RegExp(pattern.source, 'i');
    if (regex.test(html)) {
      return true;
    }
  }
  return false;
}
